<script setup lang="ts">
  import FormCheckbox from '@/components/form/FormCheckbox.vue';
  import FormMultiselect from '@/components/form/FormMultiselect.vue';
  import FormRadio from '@/components/form/FormRadio.vue';
  import FormSelect from '@/components/form/FormSelect.vue';
  import { useCustomPackageForm } from '@/composables/useCustomPackageForm';
  import { PackageInputLayout, PackageInputType, PackagePropertyType } from '@/models/package';

  const { loadPackageProps, validate, values, packageProps } = useCustomPackageForm();

  const resolveInputType = (inputType?: string, type?: string) => {
    if (inputType) return inputType;

    return type === PackagePropertyType.Single ? PackageInputType.Radio : PackageInputType.Select;
  };

  const resolveInputLayout = (inputLayout?: string) => {
    return inputLayout === PackageInputLayout.Vertical ? PackageInputLayout.Vertical : PackageInputLayout.Horizontal;
  };

  defineExpose({
    validate,
    values
  });

  await loadPackageProps();
</script>

<template>
  <div class="custom-package-form">
    <form novalidate>
      <div
        v-for="(packageProp, i) in packageProps"
        :key="i"
        class="custom-package-form-block">
        <p class="custom-package-form-block__title">{{ packageProp.title }}</p>
        <p
          v-if="packageProp.description"
          class="custom-package-form-block__description">
          {{ packageProp.description }}
        </p>
        <div
          class="custom-package-form-block__items"
          :data-input-layout="
            resolveInputType(packageProp.inputType, packageProp.type) === PackageInputType.Radio
              ? resolveInputLayout(packageProp.inputLayout)
              : undefined
          ">
          <template v-if="resolveInputType(packageProp.inputType, packageProp.type) === PackageInputType.Select">
            <FormSelect
              v-if="packageProp.type === PackagePropertyType.Single"
              :name="packageProp.key"
              :label="packageProp.title"
              :items="packageProp.items.map((item) => ({ label: item.name, value: item.value }))"
              :selected-item="packageProp.items[0]?.value" />
            <FormMultiselect
              v-else
              :name="packageProp.key"
              :label="packageProp.title"
              :placeholder="packageProp.label"
              :items="packageProp.items"
              option-label="name"
              option-value="value" />
          </template>

          <template v-else-if="resolveInputType(packageProp.inputType, packageProp.type) === PackageInputType.Radio">
            <FormRadio
              v-for="(item, itemIndex) in packageProp.items"
              :key="`${item.value}-${itemIndex}`"
              :name="packageProp.key"
              :checked-value="item.value"
              :label="item.name"
              :description="item.description"
              :input-layout="resolveInputLayout(packageProp.inputLayout)"
              :checked="itemIndex === 0" />
          </template>

          <template v-else>
            <FormCheckbox
              v-for="(item, itemIndex) in packageProp.items"
              :key="`${item.value}-${itemIndex}`"
              :additional-class="'form-checkbox--custom'"
              :name="packageProp.key"
              :checked-value="item.value"
              :description="item.description"
              :label="item.name" />
          </template>
        </div>
      </div>
    </form>
  </div>
</template>
